import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <span className="font-mono text-xs uppercase tracking-widest text-orange-500">— Contact</span>
        <h1 className="font-display text-5xl sm:text-6xl uppercase mt-2 mb-8">Talk To The<br />Workshop.</h1>
        <p className="text-zinc-300 mb-10 leading-relaxed">
          Got a specific build in mind, or a question about your engine? Drop us a line and we'll
          come back to you.
        </p>
        <ul className="space-y-5">
          <li className="flex items-start gap-3">
            <Mail size={20} className="text-orange-500 mt-1" />
            <div>
              <div className="font-mono text-xs uppercase text-zinc-500">Email</div>
              <div>kpmxracing@gmail.com</div>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <Phone size={20} className="text-orange-500 mt-1" />
            <div>
              <div className="font-mono text-xs uppercase text-zinc-500">Phone</div>
              <a href="tel:+447453909299" className="hover:text-orange-500">07453 909299</a>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <MapPin size={20} className="text-orange-500 mt-1" />
            <div>
              <div className="font-mono text-xs uppercase text-zinc-500">Workshop</div>
              <div>United Kingdom</div>
            </div>
          </li>
        </ul>
      </div>
      <form onSubmit={onSubmit} data-testid="contact-form" className="md:col-span-7 border border-white/10 bg-zinc-900/40 p-8 md:p-10 space-y-5">
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Name</label>
          <input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 w-full bg-zinc-950 border border-white/10 focus:border-orange-500 focus:ring-2 focus:ring-orange-600/30 outline-none px-4 py-3 text-white" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Email</label>
            <input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full bg-zinc-950 border border-white/10 focus:border-orange-500 focus:ring-2 focus:ring-orange-600/30 outline-none px-4 py-3 text-white" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Phone (optional)</label>
            <input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-2 w-full bg-zinc-950 border border-white/10 focus:border-orange-500 focus:ring-2 focus:ring-orange-600/30 outline-none px-4 py-3 text-white" />
          </div>
        </div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-zinc-400">Message</label>
          <textarea required rows={6} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="mt-2 w-full bg-zinc-950 border border-white/10 focus:border-orange-500 focus:ring-2 focus:ring-orange-600/30 outline-none px-4 py-3 text-white" />
        </div>
        <button type="submit" disabled={submitting} data-testid="contact-submit"
          className="btn-hard bg-orange-600 hover:bg-orange-500 text-white px-8 py-4 font-display uppercase tracking-widest text-sm disabled:opacity-50">
          {submitting ? "Sending..." : "Send Message"}
        </button>
      </form>
    </div>
  );
}
